<template>
<Layout>
  <div class="plan-grid-header">
    <div class="week-nav-icon-wrapper" :class="changingWeeks ? 'changing-weeks' : ''" @click="decrementOffset">
      <BaseIcon name="angle-double-left" size="lg" />
    </div>
    <h3>{{ currentPlan.weekOf || 'Week of...' }}</h3>
    <div class="week-nav-icon-wrapper" :class="changingWeeks ? 'changing-weeks' : ''" @click="incrementOffset">
      <BaseIcon name="angle-double-right" size="lg" />
    </div>
  </div>

  <div class="mealtime-icons-wrapper" ref="mealtimeIcons">
    <PlanMealtimeIcons :isEmbed="this.isEmbed" :onTabletUp="el.is.tabletUp" />
  </div>

  <div class="movable-grids" ref="grid" @scroll="onGridScroll">
    <PlanGrid
      class="grid-tile-A"
      :class="getCurrentState(0)"
      :planGetter="getPlanGetter(0)"
      :isEmbed="this.isEmbed"
      @showLastWeek="decrementOffset"
      @showNextWeek="incrementOffset"
    />
    <PlanGrid
      class="grid-tile-B"
      :class="getCurrentState(1)"
      :planGetter="getPlanGetter(1)"
      :isEmbed="this.isEmbed"
      @showLastWeek="decrementOffset"
      @showNextWeek="incrementOffset"
    />
    <PlanGrid
      class="grid-tile-C"
      :class="getCurrentState(2)"
      :planGetter="getPlanGetter(2)"
      :isEmbed="this.isEmbed"
      @showLastWeek="decrementOffset"
      @showNextWeek="incrementOffset"
    />
  </div>
</Layout>
</template>

<script>
import Layout from 'layouts/main';
import PlanGrid from 'PlanGrid/PlanGrid';
import { breakpointsMixin } from 'breakpointsMixin';
import PlanMealtimeIcons from 'PlanGrid/PlanMealtimeIcons';
import { dishesMethods, favoritesMethods, platelistMethods, planComputed, planMethods, userMethods, kitchenDrawerMethods, mainMethods } from '@state/helpers';

export default {
  mixins: [breakpointsMixin],
  components: {
    Layout,
    PlanGrid,
    PlanMealtimeIcons,
  },
  props: {
    isEmbed: {
      type: Boolean,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
  },
  page() {
    return {
      title: this.user.name,
      meta: [
        {
          name: 'description',
          content: `The user profile for ${this.user.name}.`,
        },
      ],
    };
  },
  computed: {
    ...planComputed,
  },
  async mounted() {
    this.showLoader(this.$t('lblPreparingYourPlan'));
    await this.getPlanGrid();
    await this.getDishes();
    await this.getFilterOpts();
    await this.getFavorites();
    await this.getPlatelist();
    await this.getUserProfile();
    this.hideLoader();
  },
  methods: {
    ...dishesMethods,
    ...favoritesMethods,
    ...platelistMethods,
    ...planMethods,
    ...userMethods,
    ...kitchenDrawerMethods,
    ...mainMethods,
    async decrementOffset() {
      if (!this.changingWeeks) {
        await this.getPreviousWeekPlanGrid();
      }
    },
    async incrementOffset() {
      if (!this.changingWeeks) {
        await this.getNextWeekPlanGrid();
      }
    },
    getCurrentState(gridIndex) {
      switch (gridIndex) {
        case 0:
          switch (this.currentPlanBufferIndex) {
            case 0:
              return 'middle-grid';
            case 1:
              return 'left-grid';
            case 2:
              return 'right-grid';
          }
          break;
        case 1:
          switch (this.currentPlanBufferIndex) {
            case 0:
              return 'right-grid';
            case 1:
              return 'middle-grid';
            case 2:
              return 'left-grid';
          }
          break;
        case 2:
          switch (this.currentPlanBufferIndex) {
            case 0:
              return 'left-grid';
            case 1:
              return 'right-grid';
            case 2:
              return 'middle-grid';
          }
          break;
      }
    },
    getPlanGetter(gridIndex) {
      switch (gridIndex) {
        case 0:
          switch (this.currentPlanBufferIndex) {
            case 0:
              return this.currentPlan;
            case 1:
              return this.previousPlan;
            case 2:
              return this.nextPlan;
          }
          break;
        case 1:
          switch (this.currentPlanBufferIndex) {
            case 0:
              return this.nextPlan;
            case 1:
              return this.currentPlan;
            case 2:
              return this.previousPlan;
          }
          break;
        case 2:
          switch (this.currentPlanBufferIndex) {
            case 0:
              return this.previousPlan;
            case 1:
              return this.nextPlan;
            case 2:
              return this.currentPlan;
          }
          break;
      }
    },
    onGridScroll() {
      this.$refs.mealtimeIcons.style.top = `-${this.$refs.grid.scrollTop}px`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'theme.scss';

.plan-grid-header {
  background-color: transparent;
  display: flex;
  height: 6rem;
  justify-content: flex-end;
  padding: 1rem;
  position: fixed;
  right: 36.5rem;
  top: 0.2rem;
  width: 28rem;
  z-index: 2;

  h3 {
    color: $color-plan-grid-header-text-desktop;
    font-size: 1.7rem;
    font-weight: 600;
    line-height: 2.4;
    text-align: center;
    width: 14rem;
  }

  .week-nav-icon-wrapper {
    align-items: center;
    background-color: $color-plan-week-nav-icon-bg;
    border: 2px solid $color-plan-week-nav-icon-border;
    border-radius: 50rem;
    display: flex;
    height: 40px;
    justify-content: center;
    width: 40px;

    &:first-of-type {
      margin-right: 2rem;
    }

    &:last-of-type {
      margin-left: 2rem;
    }

    &:hover {
      cursor: pointer;
    }

    &.changing-weeks {
      background-color: rgba($color-plan-week-nav-icon-bg, 0.5);
      border: 2px solid rgba($color-plan-week-nav-icon-border, 0.5);

      svg {
        color: rgba($color-blackText, 0.5);
      }
    }
  }
}

.mealtime-icons-wrapper {
  position: relative;
}

.movable-grids {
  height: calc(100% - 6rem);
  margin-bottom: 4rem;
  overflow-x: hidden;
  overflow-y: scroll;
  position: absolute;
  width: 142rem;

  [class*='grid-tile'] {
    display: block;
    position: absolute;
    transition: all 2s;
    width: 130rem;

    &.left-grid {
      left: -150rem;
      opacity: 0;
    }

    &.middle-grid {
      left: 0;
      opacity: 1;
    }

    &.right-grid {
      left: 150rem;
      opacity: 0;
    }
  }
}
</style>
